<script setup lang="ts">
    import { reactive, ref } from 'vue';
    import { MeshBasicMaterial, OrthographicCamera, TextureLoader } from 'three';
    import {
        prototypeXraySceneContext,
        prototypeXraySceneState,
    } from '@/views/prototype/prototypeLandmarksScene';
    import { prototypeLandmarks } from './prototypeLandmarks';
    import { updateNode } from '@/lib/threeScene/updateNode';
    import { useScene } from '@/lib/threeScene/scene/useScene';
    import { attachRenderingState } from '@/lib/threeScene/scene/attachRenderingState';
    import testXrayUrl from './test_xray.jpg';

    const landmarksData = reactive(prototypeLandmarks);
    const sceneState = reactive(prototypeXraySceneState());

    const landmarkPositions = ref(
        prototypeLandmarks.landmarks.map((landmark) => ({
            x: landmark.x,
            y: landmark.y,
            z: 0,
        })),
    );

    const { containerRef, canvasRef } = useScene({
        camera: () => new OrthographicCamera(),
        sceneContext: () => prototypeXraySceneContext(sceneState),
        setup: ({ canvas, container, sceneContext, camera, runtime }) => {
            new TextureLoader().load(testXrayUrl, (texture) => {
                const plane = sceneState.nodes.imagePlane;
                plane.material = new MeshBasicMaterial({ map: texture });
                plane.width = 1;
                plane.height = 1;
                plane.visible = true;
                updateNode(sceneContext, plane);
            });

            sceneState.nodes.landmarks.forEach((node) => updateNode(sceneContext, node));

            return attachRenderingState({
                runtime,
                state: sceneState.rendering,
                scene: sceneContext.scene,
                camera,
                container,
                canvas,
            });
        },
    });
</script>

<template>
    <div ref="containerRef" class="viewport-container">
        <canvas ref="canvasRef" class="viewport-canvas" />

        <v-card class="landmarks-sidebar pa-4" theme="dark" width="280">
            <v-card-title class="px-0 pb-2 text-h6">Landmarks</v-card-title>
            <v-divider></v-divider>
            <v-list bg-color="transparent">
                <v-list-item v-for="(l, i) in landmarksData.landmarks" :key="l.landmark_name" class="px-0 mb-2">
                    <div class="text-subtitle-2 font-weight-bold primary--text">{{ l.landmark_name }}</div>
                    <div v-if="landmarkPositions[i]" class="text-caption">
                        x: {{ landmarkPositions[i].x.toFixed(2) }},
                        y: {{ landmarkPositions[i].y.toFixed(2) }}
                    </div>
                </v-list-item>
            </v-list>
        </v-card>
    </div>
</template>

<style scoped>
    .viewport-container {
        position: absolute;
        width: 100%;
        height: 100%;
    }

    .viewport-canvas {
        position: absolute;
    }

    .landmarks-sidebar {
        position: absolute;
        right: 20px;
        top: 20px;
        background-color: rgba(30, 30, 30, 0.8) !important;
        border: 1px solid rgba(255, 255, 255, 0.1);
        backdrop-filter: blur(10px);
        z-index: 10;
    }
</style>